import { PropTypes } from 'prop-types';
import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectCoreSiteId } from '../../settings/reducer';
import moment from 'moment';
import { selectMultiSiteSelection } from '../../site-selection/reducer';
import HourlyWashCountsTable from './HourlyWashCountsTable';

const HourlyWashCountsDataGetter = (props) => {
  const coreSiteId = useSelector(selectCoreSiteId),
    multiSites = useSelector(selectMultiSiteSelection);

  // Use ref to store abort controller for cleanup
  const abortControllerRef = useRef(null);
  const timeoutRef = useRef(null);
  const lastFetchParamsRef = useRef(null);

  // Memoize the query parameters to prevent unnecessary API calls
  const queryParams = useMemo(
    () => ({
      fromDate: props.fromDate,
      untilDate: props.untilDate,
      siteId: coreSiteId,
      multiSites: multiSites?.length > 0,
    }),
    [props.fromDate, props.untilDate, coreSiteId, multiSites]
  );

  // Check if we need to fetch new data
  const shouldFetch = useCallback(() => {
    const currentParams = JSON.stringify(queryParams);
    const lastParams = lastFetchParamsRef.current;

    // Don't fetch if parameters haven't changed
    if (lastParams === currentParams) {
      return false;
    }

    // Don't fetch if we don't have required data
    if (!coreSiteId || !props.fromDate || !props.untilDate) {
      return false;
    }

    return true;
  }, [queryParams, coreSiteId, props.fromDate, props.untilDate]);

  // Memoize the fetch function to prevent unnecessary re-renders
  const getHourlyWashCounts = useCallback(() => {
    if (!shouldFetch()) {
      return;
    }

    const { fetchHourlyWashCounts } = props;

    // Abort previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    // Update last fetch params
    lastFetchParamsRef.current = JSON.stringify(queryParams);

    // Fetch data
    fetchHourlyWashCounts(
      moment(props.fromDate).startOf('day').format('YYYY-MM-DD'),
      moment(props.untilDate).endOf('day').format('YYYY-MM-DD'),
      abortControllerRef.current.signal
    );
  }, [props.fetchHourlyWashCounts, shouldFetch, queryParams, props.fromDate, props.untilDate]);

  // Debounced fetch function
  const debouncedFetch = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      getHourlyWashCounts();
    }, 300); // 300ms debounce
  }, [getHourlyWashCounts]);

  // Cleanup function to abort any pending requests
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  useEffect(() => {
    debouncedFetch();

    // Cleanup function to abort requests when component unmounts
    return cleanup;
  }, [debouncedFetch, cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Don't render if we don't have required data
  if (!coreSiteId || !props.fromDate || !props.untilDate) {
    return null;
  }

  return (
    <HourlyWashCountsTable {...props} intl={props.intl} hourlyWashCounts={props.hourlyWashCounts} />
  );
};

HourlyWashCountsDataGetter.propTypes = {
  intl: PropTypes.shape({}).isRequired,
  fromDate: PropTypes.string.isRequired,
  untilDate: PropTypes.string.isRequired,
  fetchHourlyWashCounts: PropTypes.func.isRequired,
  hourlyWashCounts: PropTypes.shape({}).isRequired,
};

export default HourlyWashCountsDataGetter;
